import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate, useLocation } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllWorkSchedules, deleteWorkSchedule, printJobCompletionReport, printWorkScheduleReport } from "../../services/WorkScheduleServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { displayError } from "../../helpers";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { buildWorkScheduleQueryParams, DEFAULT_LIST_VIEW_CONFIG } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatRecordStatus, splitRecordsByStatus, sumField, formatQty } from "../../utils/listSummaryUtils";
import moment from 'moment';

const mapWorkScheduleRows = (workScheduleList) => (
    (workScheduleList || []).map((ws) => {
        const isActive = Boolean(ws.status_2);
        return {
            id: ws.id,
            plant: ws.plant?.alias_name || '',
            schedule_no: ws.schedule_no,
            schedule_date: ws.schedule_date,
            scheduled_qty: ws.scheduled_qty,
            accepted_qty: ws.accepted_qty || 0,
            supplied_qty: ws.supplied_qty || 0,
            order_quantity: ws.sales_order_detail?.order_quantity,
            balance_qty: ws.balance_qty,
            delivery_mode: ws.delivery_mode?.toUpperCase() || '',
            order_no: `${ws.sales_order_detail?.prefix || ''}${ws.sales_order_detail?.order_no || ''}`,
            order_date: ws.sales_order_detail?.order_date,
            delivery_date: ws.delivery_date,
            delivery_time: ws.delivery_time,
            consignee: ws.sales_order_detail?.consignee?.project_name || '',
            customer: ws.sales_order_detail?.consignee?.customer?.name || '',
            grade: ws.sales_order_detail?.grade || ws.sales_order_detail?.product?.name || '',
            concrete_structure: ws.sales_order_detail?.concrete_structure || '',
            salesrep: ws.sales_order_detail?.consignee?.customer?.salesrep?.name || '',
            salesrep_alias: ws.sales_order_detail?.consignee?.customer?.salesrep?.alias || '',
            pump: ws.pump?.name || '',
            status: ws.status,
            status_2: formatRecordStatus(isActive),
            is_inactive: !isActive,
            is_schedule_completed: ws.is_schedule_completed ? 'Yes' : 'No',
            hide_row_edit: ws.is_schedule_completed ? 'Yes' : 'No',
            hide_row_print: 'No',
        };
    })
);

const serializeListViewConfig = (config) => JSON.stringify(config || {});

function WorkScheduleTable() {
    const InitialData = {
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const location = useLocation();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true); 

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const todayRecords = useMemo(
        () => data.records.filter((row) => row.delivery_date === todayDateLabel),
        [data.records, todayDateLabel],
    );

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(
        () => buildGradientCard('Today Sch.', formatQty(sumField(todayRecords, 'scheduled_qty')), 'today'),
        [todayRecords],
    );

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total', data.records.length, 'blue'),
            buildGradientCard('Scheduled', formatQty(sumField(activeRecords, 'scheduled_qty')), 'purple'),
            buildGradientCard('Delivered', formatQty(sumField(activeRecords, 'supplied_qty')), 'teal'),
            buildGradientCard('Accepted', formatQty(sumField(activeRecords, 'accepted_qty')), 'blue'),
        ];
        const cancelledQty = sumField(cancelledRecords, 'scheduled_qty');
        if (cancelledRecords.length && cancelledQty > 0) {
            cards.push(buildGradientCard('Cancelled Sch. Qty', formatQty(cancelledQty), 'red'));
        }
        return cards;
    }, [data.records.length, activeRecords, cancelledRecords]);

    const right_pinned = ["id"];

    const columns = useMemo(
        () => [
            {
                fieldName: 'plant', 
                headerName: 'Plant'
            },
            {
                fieldName: 'schedule_no', 
                headerName: 'Job No'
            },
            {
                fieldName: 'order_no',
                headerName: 'SO No.'
            },
            {
                fieldName: "delivery_date",
                headerName: 'Delivery Date'
            },
            {
                fieldName: "delivery_time",
                headerName: 'Delivery Time'
            },
            {
                fieldName: "customer",
                headerName: "Customer"
            },
            {
                fieldName: "consignee",
                headerName: "Site"
            },
            {
                fieldName: "scheduled_qty",
                headerName: "Scheduled Qty"
            },
            {
                fieldName: "balance_qty",
                headerName: "Balance Qty"
            },
            {
                fieldName: "grade",
                headerName: "Grade"
            },
            {
                fieldName: "delivery_mode",
                headerName: "Mode Of Delivery"
            },
            {
                fieldName: "salesrep_alias",
                headerName: "CSM"
            },
            {
                fieldName: "concrete_structure",
                headerName: "Concrete Structure"
            },
            {
                fieldName: "status",
                headerName: "Status"
            },
            {
                fieldName: "status_2",
                headerName: "Record Status"
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
            // {
            //     fieldName: "is_schedule_completed",
            //     headerName: "Is Schedule completed?"
            // },
                        // {
            //     fieldName: "schedule_date",
            //     headerName: 'Job Date'
            // },
            // {
            //     fieldName: "order_no",
            //     headerName: 'SalesOrder No'
            // },
            // {
            //     fieldName: "order_date",
            //     headerName: "SalesOrder Date"
            // },
             // {
            //     fieldName: "order_quantity",
            //     headerName: "Order Qty"
            // },
            // {
            //     fieldName: "supplied_qty",
            //     headerName: "Delivered Qty"
            // },
            // {
            //     fieldName: "accepted_qty",
            //     headerName: "Accepted Qty"
            // },
            // {
            //     fieldName: "pump",
            //     headerName: "Pump No"
            // },
        ],
        [],);

    const onEditWorkSchedule = (workscheduleId, event) => {
       navigate(`/WorkScheduleEdit/${workscheduleId}/`);
    };

    const openWorkSchedulePdf = async (workscheduleId, scheduleNo) => {
        try {
            const blob = await printWorkScheduleReport(cookies, workscheduleId);
            openPdfResponse(blob, {
                title: 'Work Schedule Report',
                downloadFileName: `WorkSchedule_${scheduleNo || workscheduleId}.pdf`,
            });
        } catch (error) {
            let message = error?.response?.data ?? error.message;
            if (message instanceof Blob) {
                try {
                    const text = await message.text();
                    message = JSON.parse(text);
                } catch {
                    message = 'PDF generation failed on server.';
                }
            }
            displayError(message, 'Print Failed');
        }
    };

    const onPrintWorkSchedule = async (workscheduleId, event) => {
        event?.preventDefault?.();
        const row = data.records.find((record) => record.id === workscheduleId);
        await openWorkSchedulePdf(workscheduleId, row?.schedule_no);
    };

    const openJobCompletionPdf = async (workscheduleId) => {
        try {
            const blob = await printJobCompletionReport(cookies, workscheduleId);
            openPdfResponse(blob, {
                title: 'Job Completion Report',
                downloadFileName: `JobCompletion_${workscheduleId}.pdf`,
            });
        } catch (error) {
            let message = error?.response?.data ?? error.message;
            if (message instanceof Blob) {
                try {
                    const text = await message.text();
                    message = JSON.parse(text);
                } catch {
                    message = 'PDF generation failed on server.';
                }
            }
            displayError(message, 'Print Failed');
        }
    };

    const onPrintJobCompletion = async (workscheduleId, event) => {
        event?.preventDefault?.();
        await openJobCompletionPdf(workscheduleId);
    };

    const onCompletedStatusClick = (workscheduleId) => {
        openJobCompletionPdf(workscheduleId);
    };

    const onToCompleteStatusClick = (workscheduleId) => {
        navigate(`/JobCompletion/${workscheduleId}`, {
            state: { returnTo: '/WorkScheduleTable' },
        });
    };

    const onDeleteWorkSchedule = (workscheduleId, event) => {
        event.preventDefault();
        Swal.fire({title: 'Are you sure to Delete?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 

            setIsLoading(true);
            deleteWorkSchedule(cookies,workscheduleId)
            .then(response => {
                console.log(`workschedule with id ${workscheduleId} deleted`);
                setRefreshKey(oldKey => oldKey +1);
                Swal.fire('Deleted Successfully!', '', 'success');
            })
            .catch(error => {
                setRefreshKey(oldKey => oldKey +1);
                console.log(error.response.data);
                displayError(error.response.data,'Delete Failed');
            }); 
            
            
          } else if (result.isDismissed) {    
            Swal.fire('Not Deleted', '', 'info')  
        }
        });
    };

    const handleCSVExport = (event) => {
        console.log('CSV Export');
        exportAsCSV(
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(c => c.headerName),
            data.records.map(ws => {
                const csvRow = {
                    plant:ws.plant,
                    schedule_no: ws.schedule_no,
                    schedule_date: ws.schedule_date,
                    scheduled_qty:ws.scheduled_qty,
                    order_quantity:ws.order_quantity,
                    supplied_qty:ws.supplied_qty,
                    accepted_qty:ws.accepted_qty,
                    balance_qty:ws.balance_qty,
                    delivery_mode:ws.delivery_mode.toUpperCase(),
                    order_no:ws.order_no,
                    order_date:ws.order_date,
                    consignee:ws.consignee,
                    pump:ws.pump,
                    customer:ws.customer,
                    status:ws.status,
                    salesrep:ws.salesrep
                }
                return csvRow;
                }));
    };

    const handlePDFExport = (event) => {
        console.log('PDF Export');
        exportAsPDF(
            'List of WorkShedule Form',
            columns
            .filter(col => col.headerName !== 'ROW_ACTIONS')
            .map(col=>({header: col.headerName, dataKey: col.fieldName })),
            data.records.map(ws => ({
                plant:ws.plant,
                schedule_no: ws.schedule_no,
                schedule_date: ws.schedule_date,
                scheduled_qty:ws.scheduled_qty,
                supplied_qty:ws.supplied_qty,
                accepted_qty:ws.accepted_qty,
                order_quantity:ws.order_quantity,
                balance_qty:ws.balance_qty,
                delivery_mode:ws.delivery_mode.toUpperCase(),
                order_no:ws.order_no,
                order_date:ws.order_date,
                customer:ws.customer,
                consignee:ws.consignee,
                pump:ws.pump,
                status:ws.status,
                salesrep:ws.salesrep
            })),
            'WorkScheduleList.pdf'
        );
    };

    const loadWorkScheduleList = useCallback(async (config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return [];
        }
        const activeConfig = config || DEFAULT_LIST_VIEW_CONFIG;
        const response = await getAllWorkSchedules(cookies, buildWorkScheduleQueryParams(activeConfig));
        return mapWorkScheduleRows(response?.work_schedule_list);
    }, [cookies]);

    const applyWorkScheduleRecords = useCallback((records) => {
        setData({
            total: records.length,
            records,
        });
    }, []);

    const loadWorkSchedulePage = useCallback(async () => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const defaultConfig = getDefaultListViewConfig('work_schedule');
        const defaultConfigKey = serializeListViewConfig(defaultConfig);

        try {
            const [settingsResult, defaultRecords] = await Promise.all([
                getListViewSettings(cookies, 'work_schedule').catch(() => null),
                loadWorkScheduleList(defaultConfig),
            ]);

            const resolvedConfig = settingsResult?.config || defaultConfig;
            let records = defaultRecords;

            if (serializeListViewConfig(resolvedConfig) !== defaultConfigKey) {
                records = await loadWorkScheduleList(resolvedConfig);
            }

            applyWorkScheduleRecords(records);
        } catch (error) {
            applyWorkScheduleRecords([]);
            displayError(error.response?.data ?? error.message, 'Failed to load work schedules');
        } finally {
            setIsLoading(false);
        }
    }, [applyWorkScheduleRecords, cookies, loadWorkScheduleList]);

    const handleSettingsSaved = useCallback(async (config) => {
        setIsLoading(true);
        try {
            const records = await loadWorkScheduleList(config);
            applyWorkScheduleRecords(records);
        } catch (error) {
            applyWorkScheduleRecords([]);
            displayError(error.response?.data ?? error.message, 'Failed to load work schedules');
        } finally {
            setIsLoading(false);
        }
    }, [applyWorkScheduleRecords, loadWorkScheduleList]);

    useEffect(() => {
        if (location.state?.refresh) {
            setRefreshKey((oldKey) => oldKey + 1);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        loadWorkSchedulePage();
    }, [cookies.myToken, loadWorkSchedulePage, refreshKey]);
    
    return (

        <div id="outer-container"  className="App" > 
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="PLANNING"
                    title="List of Schedules"
                    viewKey="work_schedule"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/WorkSchedule"
                    addNewLabel="New Work Schedule"
                />

                <ListPageGradientSummary
                    highlightCard={highlightCard}
                    cards={summaryCards}
                />

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='LIST OF SCHEDULES'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditWorkSchedule}
                            onRowDelete={onDeleteWorkSchedule}
                            onRowPrint={onPrintWorkSchedule}
                            onCompletedStatusClick={onCompletedStatusClick}
                            onToCompleteStatusClick={onToCompleteStatusClick}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            hidePrint={false}
                            right_pinned={right_pinned}
                            compactColumns={true}
                        />
                    </div>
                </div>
            </div>
            <button
                style={{ position: 'relative', right: '40px' }}
                className="btn btn-fill btn-primary float-end"
                type="button"
                onClick={() => { navigate('/WorkSchedule'); }}
            >
                Back
            </button>
        </div>
    );
}

export default WorkScheduleTable;